import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { Eye, Info } from "lucide-react";
import type React from "react";
import type { Invoice } from "@/app/billing/types";
import { getStatusConfig } from "./utils";

type UpcomingInvoiceSectionProps = {
  invoice: Invoice;
  formatDate: (date?: string | null) => string;
  formatCurrency: (amount: string, currency?: string) => string;
};

export const UpcomingInvoiceSection: React.FC<UpcomingInvoiceSectionProps> = ({
  invoice,
  formatDate,
  formatCurrency,
}) => {
  const statusConfig = getStatusConfig(invoice.status);

  return (
    <div className="mb-8 rounded-lg border bg-muted/30 p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <span className="font-bold text-base">Upcoming Invoice</span>
          </div>
          <Badge
            className={`text-xs ${statusConfig.className}`}
            variant={statusConfig.variant}
          >
            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
          </Badge>
        </div>
        <div className="text-right">
          <div className="font-semibold text-lg">
            {formatCurrency(invoice.total, invoice.currency)}
          </div>
          {invoice.amount_due !== "0.00" && (
            <div className="text-muted-foreground text-xs">
              Due: {formatCurrency(invoice.amount_due, invoice.currency)}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
        <div>
          <span className="text-muted-foreground">Billed Services:</span>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-bold text-lg">
              {invoice.line_items.length}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      aria-label="Show billed services details"
                      className="flex items-center rounded-full p-1 transition hover:bg-muted focus:bg-muted focus:outline-none"
                      style={{ lineHeight: 0 }}
                      type="button"
                    />
                  }
                >
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm p-4" side="top">
                  <div className="mb-2 font-semibold text-sm">
                    Billed Services Details
                  </div>
                  {invoice.line_items.length > 0 ? (
                    <div className="max-h-60 space-y-2 overflow-y-auto">
                      {invoice.line_items.map((item) => (
                        <div
                          className="flex items-center justify-between gap-8 border-b py-1 last:border-b-0"
                          key={item.id}
                        >
                          <span className="min-w-[140px]">{item.name}</span>
                          <span className="font-mono">
                            {formatCurrency(item.amount, invoice.currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      No billed services
                    </span>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div>
          <span className="text-muted-foreground">Due Date:</span>
          <div className="font-medium">{formatDate(invoice.due_date)}</div>
        </div>
        {!!invoice.scheduled_issue_at && (
          <div>
            <span className="text-muted-foreground">Scheduled:</span>
            <div className="font-medium">
              {formatDate(invoice.scheduled_issue_at)}
            </div>
          </div>
        )}
        {!!invoice.hosted_invoice_url && (
          <div className="flex justify-end">
            <Button
              render={
                // biome-ignore lint/a11y/useAnchorContent: children rendered by Base UI render prop
                <a
                  className="flex items-center gap-2"
                  href={invoice.hosted_invoice_url}
                  rel="noopener noreferrer"
                  target="_blank"
                />
              }
              size="sm"
              variant="outline"
            >
              <Eye className="h-3 w-3" />
              Preview
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
